import { useTranslation } from 'react-i18next';

import { cn } from '@alamo/ui';

interface AdminDailyDigestBodyProps {
  metadata: Record<string, unknown> | null;
}

interface DigestMetricRow {
  label: string;
  value: number;
  nested?: boolean;
}

export interface AdminDailyDigestMetrics {
  newQuotationsCount: number;
  acceptedQuotationsCount: number;
  analyzedProposalsCount: number;
  analysisDecisionsCount?: number;
  approvedProposalsCount: number;
  rejectedProposalsCount: number;
  pendingAnalysesCount?: number;
  paidActivationFeesCount: number;
  inProcessBeforeAnalysisCount: number;
  awaitingAnalysisCount: number;
  pendingProposalsCount: number;
  rejectedCurrentProposalsCount: number;
}

const ADMIN_DAILY_DIGEST_TYPES = new Set(['admin_daily_digest', 'admin_daily_digest_test']);

export function isAdminDailyDigestType(type: string): boolean {
  return ADMIN_DAILY_DIGEST_TYPES.has(type);
}

export function readAdminDailyDigestMetrics(
  metadata: Record<string, unknown> | null,
): AdminDailyDigestMetrics | null {
  if (!metadata) return null;

  const keys = [
    'newQuotationsCount',
    'acceptedQuotationsCount',
    'analyzedProposalsCount',
    'approvedProposalsCount',
    'rejectedProposalsCount',
    'paidActivationFeesCount',
    'inProcessBeforeAnalysisCount',
    'awaitingAnalysisCount',
    'pendingProposalsCount',
    'rejectedCurrentProposalsCount',
  ] as const;

  if (keys.some((key) => typeof metadata[key] !== 'number')) return null;
  const hasNewMetrics =
    metadata.analysisDecisionsCount !== undefined || metadata.pendingAnalysesCount !== undefined;
  if (
    hasNewMetrics &&
    (typeof metadata.analysisDecisionsCount !== 'number' ||
      typeof metadata.pendingAnalysesCount !== 'number')
  ) {
    return null;
  }

  return {
    newQuotationsCount: metadata.newQuotationsCount as number,
    acceptedQuotationsCount: metadata.acceptedQuotationsCount as number,
    analyzedProposalsCount: metadata.analyzedProposalsCount as number,
    analysisDecisionsCount: metadata.analysisDecisionsCount as number | undefined,
    approvedProposalsCount: metadata.approvedProposalsCount as number,
    rejectedProposalsCount: metadata.rejectedProposalsCount as number,
    pendingAnalysesCount: metadata.pendingAnalysesCount as number | undefined,
    paidActivationFeesCount: metadata.paidActivationFeesCount as number,
    inProcessBeforeAnalysisCount: metadata.inProcessBeforeAnalysisCount as number,
    awaitingAnalysisCount: metadata.awaitingAnalysisCount as number,
    pendingProposalsCount: metadata.pendingProposalsCount as number,
    rejectedCurrentProposalsCount: metadata.rejectedCurrentProposalsCount as number,
  };
}

export function AdminDailyDigestBody({ metadata }: AdminDailyDigestBodyProps) {
  const { t } = useTranslation('common');
  const metrics = readAdminDailyDigestMetrics(metadata);
  if (!metrics) return null;

  const movementRows: DigestMetricRow[] = [
    {
      label: t('common:notifications.daily_digest.new_quotations'),
      value: metrics.newQuotationsCount,
    },
    {
      label: t('common:notifications.daily_digest.accepted_quotations'),
      value: metrics.acceptedQuotationsCount,
    },
    {
      label: t(
        metrics.analysisDecisionsCount === undefined
          ? 'common:notifications.daily_digest.analyses_completed'
          : 'common:notifications.daily_digest.analyses_performed',
      ),
      value: metrics.analysisDecisionsCount ?? metrics.analyzedProposalsCount,
    },
    {
      label: t('common:notifications.daily_digest.approved'),
      value: metrics.approvedProposalsCount,
      nested: true,
    },
    {
      label: t('common:notifications.daily_digest.rejected'),
      value: metrics.rejectedProposalsCount,
      nested: true,
    },
  ];
  if (metrics.pendingAnalysesCount !== undefined) {
    movementRows.push({
      label: t('common:notifications.daily_digest.pending'),
      value: metrics.pendingAnalysesCount,
      nested: true,
    });
  }
  movementRows.push({
    label: t('common:notifications.daily_digest.activation_fees_paid'),
    value: metrics.paidActivationFeesCount,
  });
  const currentRows: DigestMetricRow[] = [
    {
      label: t('common:notifications.daily_digest.with_associate'),
      value: metrics.inProcessBeforeAnalysisCount,
    },
    {
      label: t('common:notifications.daily_digest.under_review'),
      value: metrics.awaitingAnalysisCount,
    },
    {
      label: t('common:notifications.daily_digest.pending'),
      value: metrics.pendingProposalsCount,
    },
    {
      label: t('common:notifications.daily_digest.rejected'),
      value: metrics.rejectedCurrentProposalsCount,
    },
  ];
  const sections: Array<{ title: string; rows: DigestMetricRow[] }> = [
    {
      title: t('common:notifications.daily_digest.movement_title'),
      rows: movementRows,
    },
    {
      title: t('common:notifications.daily_digest.current_status_title'),
      rows: currentRows,
    },
  ];

  return (
    <div className="mt-1 space-y-2 text-xs text-slate-500" data-testid="admin-daily-digest-body">
      {sections.map((section) => (
        <section key={section.title}>
          <h4 className="mb-0.5 font-semibold text-secondary-950">{section.title}</h4>
          <dl className="space-y-0.5">
            {section.rows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <dt className={cn('min-w-0', row.nested && 'pl-3 text-slate-400')}>
                  {row.nested && <span aria-hidden>↳ </span>}
                  {row.label}
                </dt>
                <dd
                  className={cn(
                    'shrink-0 tabular-nums',
                    row.nested
                      ? 'pr-3 text-[11px] font-medium text-slate-500'
                      : 'font-semibold text-secondary-950',
                  )}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
